import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { Task } from "../../types";

export function useDeleteAllTasksInList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (listId: string) => {
      // Only the List's Owner may do this in one shot (ADR-0003) — going
      // through the RPC makes that refusal explicit and all-or-nothing,
      // rather than a plain bulk DELETE silently narrowing itself to just
      // the caller's own Tasks under row-level security.
      const { error } = await supabase.rpc("delete_all_tasks_in_list", {
        p_list_id: listId,
      });
      if (error) throw error;
    },

    onMutate: async (listId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks });
      const prevTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useTasks out of its pending state and drop the app's loading gate,
      // and onError's snapshot guard could not put it back.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (old) =>
        old ? old.filter((t) => t.list_id !== listId) : old
      );

      return { prevTasks };
    },

    onError: (err, _, context) => {
      if (context?.prevTasks)
        queryClient.setQueryData<Task[]>(queryKeys.tasks, context.prevTasks);
      Sentry.captureException(err, {
        extra: { mutation: "useDeleteAllTasksInList" },
      });
      toast.error("Failed to delete tasks. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    },
  });
}
